import React, { useMemo, useContext } from 'react'
import { App } from 'antd'
import { AqeThemeProvider, ThemeModeProvider, TokenBridge, defaultTokens } from '@pushly/aqe'
import type { AqeTokenConfig, SelectedThemeMode } from '@pushly/aqe'
import { pufferfishTokens } from '../../theme/pufferfish-tokens.config'
import { convertWhiteLabelToTokens } from '../../utils/white-label-to-tokens'
import { PlatformIdentityContext } from '../platform-identity-provider/platform-identity-provider'
import { AntdStaticApiProvider } from '../antd-app-provider/antd-static-api-provider'

export const THEME_MODE_STORAGE_KEY = 'pushly:theme-mode'
export const THEME_MODE_PREF_PATH = ['extras', 'ui:theme-mode']

function getInitialThemeMode(): SelectedThemeMode {
    try {
        const cached = localStorage.getItem(THEME_MODE_STORAGE_KEY)
        if (cached === 'light' || cached === 'dark' || cached === 'system') return cached
    } catch {
        // localStorage unavailable
    }
    return 'light'
}

export type ThemeProviderProps = {
    children: React.ReactNode
    initialThemeMode?: SelectedThemeMode
}

/**
 * Pufferfish theme provider that wraps AQE theme provider
 * Merges: AQE defaults ← Pufferfish brand ← White-label runtime overrides
 */
export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children, initialThemeMode }) => {
    const platformIdentity = useContext(PlatformIdentityContext)

    const mergedTheme = useMemo<AqeTokenConfig>(() => {
        // Start with AQE defaults
        let theme: AqeTokenConfig = {
            token: { ...defaultTokens.token },
            components: { ...defaultTokens.components },
        }

        // Apply Pufferfish brand overrides
        theme = {
            token: {
                ...theme.token,
                ...pufferfishTokens?.token,
            },
            components: {
                ...theme.components,
                ...pufferfishTokens?.components,
            },
        }

        // Apply white-label overrides if available
        if (platformIdentity.loaded) {
            const whiteLabelOverrides = convertWhiteLabelToTokens(platformIdentity.whiteLabelSettings)
            theme = {
                token: {
                    ...theme.token,
                    ...whiteLabelOverrides.token,
                },
                components: {
                    ...theme.components,
                    ...whiteLabelOverrides.components,
                },
            }
        }

        return theme
    }, [platformIdentity])

    return (
        <ThemeModeProvider initialMode={initialThemeMode ?? getInitialThemeMode()}>
            <AqeThemeProvider theme={mergedTheme}>
                <TokenBridge>
                    <App>
                        <AntdStaticApiProvider />
                        {children}
                    </App>
                </TokenBridge>
            </AqeThemeProvider>
        </ThemeModeProvider>
    )
}
